/**
 * Frontend helpers for attendance cut-off LOP deferral (mirrors backend/utils/attendanceCutoff).
 */

export function clampCutOffDay(day) {
  const n = Number(day);
  if (!Number.isFinite(n)) return 25;
  return Math.min(28, Math.max(1, Math.floor(n)));
}

export function normalizeCutOffConfig(policy = {}) {
  const legacySpecific = String(policy.cutOffMethod || "") === "specific";
  const enabled =
    policy.cutOffEnabled === true ||
    (legacySpecific && Number(policy.cutOffToDay || policy.cutOffDay) > 0);

  return {
    cutOffEnabled: !!enabled,
    cutOffDay: clampCutOffDay(
      policy.cutOffDay ?? policy.cutOffToDay ?? 25
    ),
    cutOffNewJoinerHandling: ["defer_lop", "block_joining", "no_adjustment"].includes(
      policy.cutOffNewJoinerHandling
    )
      ? policy.cutOffNewJoinerHandling
      : "defer_lop",
    cutOffSeparationHandling: [
      "fnf_recovery",
      "current_month_deduct",
      "no_recovery",
    ].includes(policy.cutOffSeparationHandling)
      ? policy.cutOffSeparationHandling
      : "fnf_recovery",
    cutOffExcessPayRecovery: policy.cutOffExcessPayRecovery !== false,
  };
}

/**
 * Returns a warning message if joining date falls after the cut-off day and policy blocks it.
 * @returns {string|null}
 */
export function getNewJoinerCutOffWarning(joiningDate, policy) {
  const cfg = normalizeCutOffConfig(policy || {});
  if (!cfg.cutOffEnabled || cfg.cutOffNewJoinerHandling !== "block_joining") {
    return null;
  }
  if (!joiningDate) return null;
  const d = new Date(`${String(joiningDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getDate();
  if (day <= cfg.cutOffDay) return null;

  const y = d.getFullYear();
  const m = d.getMonth(); // 0-based
  const nextMonth = m === 11 ? 0 : m + 1;
  const nextYear = m === 11 ? y + 1 : y;
  const suggested = `${nextYear}-${String(nextMonth + 1).padStart(2, "0")}-01`;

  return `Attendance cut-off is day ${cfg.cutOffDay}. Joining after the cut-off is blocked by policy. Suggested DOJ: ${suggested}.`;
}

export function cutOffExampleText(cutOffDay) {
  const day = clampCutOffDay(cutOffDay);
  return `Cut-off day ${day}${ordinal(day)}: active employees — post-cut-off LOP is deducted next month; post-cut-off new joiners — payable days are held and auto-credited next month as LOP reversal (true LOP stays unpaid); manual Change Status LOP is always deducted in the same month; leavers recover excess in F&F when enabled. Payroll period stays the calendar month.`;
}

function ordinal(n) {
  const v = Number(n) || 0;
  const s = ["th", "st", "nd", "rd"];
  const v100 = v % 100;
  return s[(v100 - 20) % 10] || s[v100] || s[0];
}
